/**
 * Synthesized ringback / ring generator using Web Audio API.
 *
 * No audio file needed - we generate the tones at runtime.
 *
 * Why this exists:
 *   When Kamailio relays a 180 Ringing without an SDP body (no early media),
 *   the calling browser doesn't get any audio from the network. We have to
 *   play a local "ringback" tone so the user knows the far side is ringing.
 *
 * Two patterns provided:
 *   - playRingback()   - US ringback tone (440+480 Hz, 2s on / 4s off).
 *                        Use on the CALLER side while waiting for answer.
 *   - playIncomingRing() - classic phone ringer (440+480 Hz, 0.4s on / 0.2s off
 *                          twice, then 4s off). Use on the CALLEE side for
 *                          incoming-call alert.
 *
 * Both return a stop() function. Always call stop() when call connects, fails,
 * or is cancelled to avoid stuck tones.
 */

let sharedCtx = null;

function getAudioContext() {
  if (sharedCtx && sharedCtx.state !== 'closed') return sharedCtx;
  const AC = window.AudioContext || window.webkitAudioContext;
  if (!AC) return null;
  sharedCtx = new AC();
  return sharedCtx;
}

/**
 * Internal: build a two-tone oscillator (US standard 440 + 480 Hz).
 */
function buildTone(ctx) {
  const gain = ctx.createGain();
  gain.gain.value = 0; // start silent, schedule gain in cadence
  gain.connect(ctx.destination);

  const osc1 = ctx.createOscillator();
  const osc2 = ctx.createOscillator();
  osc1.frequency.value = 440;
  osc2.frequency.value = 480;
  osc1.connect(gain);
  osc2.connect(gain);

  // Reasonable volume (loud tones startle people)
  const TARGET_GAIN = 0.12;

  return { gain, osc1, osc2, TARGET_GAIN };
}

/**
 * US ringback tone: 2 seconds on, 4 seconds off, repeating.
 * Used by the CALLER while the far side rings.
 */
export function playRingback() {
  const ctx = getAudioContext();
  if (!ctx) return () => {};

  // Resume in case it was suspended (Chrome autoplay policy)
  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const { gain, osc1, osc2, TARGET_GAIN } = buildTone(ctx);

  const t0 = ctx.currentTime;
  // Schedule a cadence for ~5 minutes worth of ring (60 cycles * 6s each).
  // Each cycle: 2s tone, 4s silence. We use linearRampToValueAtTime to make
  // the transitions click-free.
  for (let i = 0; i < 60; i++) {
    const cycleStart = t0 + i * 6;
    gain.gain.setValueAtTime(0, cycleStart);
    gain.gain.linearRampToValueAtTime(TARGET_GAIN, cycleStart + 0.05);
    gain.gain.setValueAtTime(TARGET_GAIN, cycleStart + 2);
    gain.gain.linearRampToValueAtTime(0, cycleStart + 2.05);
  }

  osc1.start(t0);
  osc2.start(t0);

  return function stop() {
    try {
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.05);
      osc1.stop(now + 0.1);
      osc2.stop(now + 0.1);
    } catch (e) {
      // already stopped
    }
  };
}

/**
 * Incoming-call ringer: classic US phone cadence.
 * Two short bursts (0.4s on, 0.2s off, 0.4s on), then 4s silence.
 * Used by the CALLEE on an incoming call.
 */
export function playIncomingRing() {
  const ctx = getAudioContext();
  if (!ctx) return () => {};

  if (ctx.state === 'suspended') ctx.resume().catch(() => {});

  const { gain, osc1, osc2, TARGET_GAIN } = buildTone(ctx);

  const t0 = ctx.currentTime;
  // 60 cycles * 5s each
  for (let i = 0; i < 60; i++) {
    const cs = t0 + i * 5;
    // burst 1
    gain.gain.setValueAtTime(0, cs);
    gain.gain.linearRampToValueAtTime(TARGET_GAIN, cs + 0.02);
    gain.gain.setValueAtTime(TARGET_GAIN, cs + 0.4);
    gain.gain.linearRampToValueAtTime(0, cs + 0.42);
    // burst 2
    gain.gain.setValueAtTime(0, cs + 0.6);
    gain.gain.linearRampToValueAtTime(TARGET_GAIN, cs + 0.62);
    gain.gain.setValueAtTime(TARGET_GAIN, cs + 1.0);
    gain.gain.linearRampToValueAtTime(0, cs + 1.02);
  }

  osc1.start(t0);
  osc2.start(t0);

  return function stop() {
    try {
      const now = ctx.currentTime;
      gain.gain.cancelScheduledValues(now);
      gain.gain.setValueAtTime(gain.gain.value, now);
      gain.gain.linearRampToValueAtTime(0, now + 0.05);
      osc1.stop(now + 0.1);
      osc2.stop(now + 0.1);
    } catch (e) {
      // already stopped
    }
  };
}
